import { FC } from 'react';
import styled from 'styled-components';

import Text from '@/components/Text';
import { VideoCompilerCompilationStatus } from '@/utils/enums';

const StatusText = styled(Text)`
  display: flex;
  align-items: center;
  gap: 6px;
`;

interface Props {
  status: VideoCompilerCompilationStatus;
  progress: number;
}

const CompilationStatus: FC<Props> = ({ status, progress }) => {
  if (status === VideoCompilerCompilationStatus.COMPLETED) {
    return (
      <StatusText as="div" $type="smallHeadline">
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14Z"
            fill="#42BE77"
          />
          <path
            d="M9.96214 5.38464L6.0134 9.15387L4.03906 7.26926"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Done
      </StatusText>
    );
  }

  if (status === VideoCompilerCompilationStatus.FAILED) {
    return (
      <StatusText as="div" $type="smallHeadline">
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14Z"
            fill="#F64629"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9.71064 5.70711C10.1012 5.31658 10.1012 4.68342 9.71064 4.29289C9.32011 3.90237 8.68695 3.90237 8.29642 4.29289L7.00177 5.58755L5.70711 4.29289C5.31658 3.90237 4.68342 3.90237 4.29289 4.29289C3.90237 4.68342 3.90237 5.31658 4.29289 5.70711L5.58755 7.00177L4.29289 8.29642C3.90237 8.68695 3.90237 9.32011 4.29289 9.71064C4.68342 10.1012 5.31658 10.1012 5.70711 9.71064L7.00177 8.41598L8.29642 9.71064C8.68695 10.1012 9.32011 10.1012 9.71064 9.71064C10.1012 9.32011 10.1012 8.68695 9.71064 8.29642L8.41598 7.00177L9.71064 5.70711Z"
            fill="white"
          />
        </svg>
        Failed
      </StatusText>
    );
  }

  if (status === VideoCompilerCompilationStatus.DRAFT) {
    return (
      <StatusText as="div" $type="smallHeadline">
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.83008 12.6145H1.89567C1.7603 12.6145 1.63047 12.5608 1.53475 12.465C1.43903 12.3693 1.38525 12.2395 1.38525 12.1041V1.89579C1.38525 1.76042 1.43903 1.6306 1.53475 1.53487C1.63047 1.43915 1.7603 1.38538 1.89567 1.38538H12.104C12.2394 1.38538 12.3692 1.43915 12.4649 1.53487C12.5606 1.6306 12.6144 1.76042 12.6144 1.89579V8.8302C12.6144 8.89723 12.6012 8.96361 12.5756 9.02553C12.5499 9.08746 12.5123 9.14373 12.4649 9.19112L9.191 12.465C9.14361 12.5124 9.08734 12.55 9.02541 12.5757C8.96348 12.6013 8.89711 12.6145 8.83008 12.6145V12.6145Z"
            stroke="#909090"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.5684 9.04114H9.0415V12.5685"
            stroke="#909090"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Draft
      </StatusText>
    );
  }

  return (
    <StatusText as="div" $type="smallHeadline">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14px"
        height="14px"
        viewBox="0 0 24 24"
      >
        <path
          fill="#42BE77"
          d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z"
          opacity=".25"
        ></path>
        <path
          fill="#42BE77"
          d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z"
        >
          <animateTransform
            attributeName="transform"
            dur="0.75s"
            repeatCount="indefinite"
            type="rotate"
            values="0 12 12;360 12 12"
          ></animateTransform>
        </path>
      </svg>
      {progress}%
    </StatusText>
  );
};

export default CompilationStatus;
